import React, { useState, useMemo, useEffect } from 'react';
import { 
  CheckCircle2,
  XCircle,
  Clock, 
  Search, 
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import { 
  getPendingTasks, 
  getApprovedTasks, 
  getRejectedTasks, 
  ApprovalTask 
} from '../../../services/approvalService.ts';
import { getDocumentById } from '../../../services/documentService.ts';
import { useNavigate } from 'react-router-dom';

type SortField = 'documentNumber' | 'documentTitle' | 'submittedDate' | 'daysWaiting' | 'priority' | 'approvedDate';
type SortDirection = 'asc' | 'desc';
type TabType = 'pending' | 'approved' | 'rejected';

const MyApprovalTasks: React.FC = () => {
  const navigate = useNavigate();
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  
  // Data state
  const [pendingTasks, setPendingTasks] = useState<ApprovalTask[]>([]);
  const [approvedTasks, setApprovedTasks] = useState<ApprovalTask[]>([]);
  const [rejectedTasks, setRejectedTasks] = useState<ApprovalTask[]>([]);

  // Preload all task lists on first entry
  useEffect(() => {
    const loadAllTasks = async () => {
      try {
        const [pending, approved, rejected] = await Promise.all([
          getPendingTasks(),
          getApprovedTasks(),
          getRejectedTasks(),
        ]);

        setPendingTasks(pending);
        setApprovedTasks(approved);
        setRejectedTasks(rejected);
      } catch (err: any) {
        console.error('Error loading tasks:', err);
      }
    };

    loadAllTasks();
  }, []);

  // Fallback loading by tab (when a list is empty and user switches)
  useEffect(() => {
    const loadTasksByTab = async () => {
      try {
        if (activeTab === 'pending' && pendingTasks.length === 0) {
          setPendingTasks(await getPendingTasks());
        } else if (activeTab === 'approved' && approvedTasks.length === 0) {
          setApprovedTasks(await getApprovedTasks());
        } else if (activeTab === 'rejected' && rejectedTasks.length === 0) {
          setRejectedTasks(await getRejectedTasks());
        }
      } catch (err: any) {
        console.error(`Error loading ${activeTab} tasks:`, err);
      }
    };

    loadTasksByTab();
  }, [activeTab, pendingTasks.length, approvedTasks.length, rejectedTasks.length]);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterDocType, setFilterDocType] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('daysWaiting');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ApprovalTask | null>(null);

  // Get current tab tasks
  const currentTabTasks = useMemo(() => {
    if (activeTab === 'pending') return pendingTasks;
    if (activeTab === 'approved') return approvedTasks;
    if (activeTab === 'rejected') return rejectedTasks;
    return [];
  }, [activeTab, pendingTasks, approvedTasks, rejectedTasks]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Reset selections and page when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
    setFilterPriority('all');
    setFilterDocType('all');
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = currentTabTasks.filter(task => {
      const matchesSearch = 
        (task.documentNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.documentTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.submittedBy || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
      const matchesDocType = filterDocType === 'all' || task.documentCategory === filterDocType;
      
      return matchesSearch && matchesPriority && matchesDocType;
    });

    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'priority') {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
      } else if (sortField === 'submittedDate' || sortField === 'approvedDate') {
        aValue = new Date(a[sortField] || 0).getTime();
        bValue = new Date(b[sortField] || 0).getTime();
      } else if (sortField === 'daysWaiting') {
        aValue = a.daysWaiting || 0;
        bValue = b.daysWaiting || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [currentTabTasks, searchTerm, filterPriority, filterDocType, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTasks.length / itemsPerPage);
  const paginatedTasks = filteredAndSortedTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get document type badge
  const getDocTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      SOP: 'bg-blue-100 text-blue-800 border-blue-200',
      WI: 'bg-purple-100 text-purple-800 border-purple-200',
      FORM: 'bg-green-100 text-green-800 border-green-200',
      POLICY: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[type] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {type}
      </span>
    );
  };

  const hasValidDescription = (value?: string) => {
    const normalized = String(value || '').trim();
    return normalized !== '' && normalized !== '-';
  };

  const openTaskModal = async (task: ApprovalTask) => {
    let taskForModal = task;

    try {
      if (!hasValidDescription(task.description) && task.documentId) {
        const document = await getDocumentById(task.documentId);
        const fallbackDescription = String((document as any)?.description || '').trim();
        if (fallbackDescription) {
          taskForModal = {
            ...task,
            description: fallbackDescription
          };
        }
      }
    } catch (error) {
      // Keep modal usable even if fallback fetch fails.
      console.warn('Failed to fetch document description for task modal:', error);
    }

    setSelectedTask(taskForModal);
    setShowApprovalModal(true);
  };

  // Statistics
  const stats = {
    pending: pendingTasks.length,
    approved: approvedTasks.length,
    rejected: rejectedTasks.length,
    high: pendingTasks.filter(t => t.priority === 'high').length,
    overdue: pendingTasks.filter(t => (t.daysWaiting || 0) > 5).length,
    today: pendingTasks.filter(t => t.daysWaiting === 0).length
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="size-4 opacity-0 group-hover:opacity-30" />;
    return sortDirection === 'asc' ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />;
  };

  return (
    <div className="w-full h-full overflow-auto flex flex-col bg-transparent">
      {/* Header + Tabs */}
      <div className="px-10 pt-4 pb-3 flex-shrink-0">
        <div className="rounded-2xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">My Approval Tasks</h2>
              <p className="text-sm text-slate-500 mt-1">Review and process pending workflow documents.</p>
            </div>
            <span className="inline-flex items-center h-8 px-3 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
              Total: {currentTabTasks.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-10 mb-4 rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-[0_6px_18px_rgba(15,23,42,0.05)]">
        {/* Tabs */}
        <div className="px-5 pt-4 border-b border-gray-200">
          <nav className="flex gap-4 sm:gap-8 min-w-max">
            <button
              onClick={() => handleTabChange('pending')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Clock className="size-5" />
                <span>Pending</span>
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.pending}
                </span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('approved')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5" />
                <span>Approved</span>
                <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.approved}
                </span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('rejected')}
              className={`pb-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <XCircle className="size-5" />
                <span>Rejected</span>
                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stats.rejected}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by document number, title or submitter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        </div>

      {/* Tasks Table */}
      <div className="border-t border-gray-200 overflow-hidden bg-white">
        <table className="w-full [&_th]:align-middle [&_td]:align-middle [&_th]:text-center [&_td]:text-center">
          <thead className="bg-slate-50 border-b border-gray-200">
              <tr>
                <th className="w-12 px-2 py-3">
                  &nbsp;
                </th>
                <th 
                  className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group whitespace-nowrap"
                  onClick={() => handleSort('documentNumber')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Doc #
                    <SortIcon field="documentNumber" />
                  </div>
                </th>
                <th 
                  className="max-w-[70px] px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('documentTitle')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Title
                    <SortIcon field="documentTitle" />
                  </div>
                </th>
                <th className="hidden lg:table-cell px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                  Type
                </th>
               
              
                <th 
                  className="hidden sm:table-cell px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group whitespace-nowrap"
                  onClick={() => handleSort('submittedDate')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Date
                    <SortIcon field="submittedDate" />
                  </div>
                </th>
                
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedTasks.map((task) => (
                <tr 
                  key={task.id}
                  className="hover:bg-slate-50 even:bg-[#fbfdff] transition-colors"
                >
                  <td className="px-4 py-4">
                    &nbsp;
                  </td>
                  <td className="px-2 py-4 min-w-[80px]">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 text-xs sm:text-sm">{task.documentNumber}</span>
                      <span className="text-xs text-gray-500">v{task.currentVersion}</span>
                    </div>
                  </td>
                  <td className="px-2 py-4 min-w-[180px]">
                    <div>
                      <p className="font-medium text-gray-900 truncate text-xs sm:text-sm">{task.documentTitle}</p>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell px-2 py-4">
                    {getDocTypeBadge(task.documentCategory)}
                  </td>
                 
                 
                  <td className="hidden sm:table-cell px-2 py-4 text-xs whitespace-nowrap">
                    <span className="text-xs text-gray-900">{task.submittedDate}</span>
                  </td>
                
                 
                  <td className="px-2 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => navigate(`/mytask/${task.documentId}/view`)}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        View
                      </button>
                      {(activeTab === 'approved' || activeTab === 'rejected') && (
                        <button
                          onClick={() => {
                            openTaskModal(task);
                          }}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md transition-colors"
                        >
                          Details
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        {/* Empty State */}
        {filteredAndSortedTasks.length === 0 && (
          <div className="text-center py-12">
            <FileText className="size-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No tasks found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Pagination */}
        {filteredAndSortedTasks.length > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 bg-slate-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700 ml-4">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedTasks.length)} of {filteredAndSortedTasks.length} entries
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page === 1 || 
                           page === totalPages || 
                           (page >= currentPage - 1 && page <= currentPage + 1);
                  })
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'approved' ? 'Approval Details' : 'Rejection Details'}
                </h2>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedTask(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Document Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Document Number</p>
                    <p className="font-medium text-gray-900">{selectedTask.documentNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Version</p>
                    <p className="font-medium text-gray-900">{selectedTask.currentVersion}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Title</p>
                    <p className="font-medium text-gray-900">{selectedTask.documentTitle}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Submitted By</p>
                    <p className="font-medium text-gray-900">{selectedTask.submittedBy}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Submitted Date</p>
                    <p className="font-medium text-gray-900">{selectedTask.submittedDate}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium text-gray-900">{hasValidDescription(selectedTask.description) ? selectedTask.description : '-'}</p>
                  </div>
                </div>
              </div>

              {/* Previous Approvers */}
              {selectedTask.previousApprovers && selectedTask.previousApprovers.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">Previous Approvers</p>
                  <div className="space-y-2">
                    {selectedTask.previousApprovers.map((approver, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        •
                        {approver}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show approval comment for approved/rejected tabs */}
              {(activeTab === 'approved' || activeTab === 'rejected') && selectedTask.approvalComment && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Comment
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-900">{selectedTask.approvalComment}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedTask(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApprovalTasks;
